import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SignIn } from './pages/SignIn';
import { Dashboard } from './pages/Dashboard';
import { Workspace } from './pages/Workspace';
import { SignupsAdmin } from './pages/SignupsAdmin';
import { PostAuthModal } from './components/PostAuthModal';
import { ConsentGate } from './components/ConsentGate';
import { DocEditorTest } from './pages/test/DocEditorTest';
import { StoryBrowserTest } from './pages/test/StoryBrowserTest';
import { DashboardTest } from './pages/test/DashboardTest';
import { StoryDetailTest } from './pages/test/StoryDetailTest';
import { GenericDocTest } from './pages/test/GenericDocTest';
import { ProjectSettingsTest } from './pages/test/ProjectSettingsTest';
import { config } from './config';
import { identifyUser, resetUser, trackPageView } from './lib/posthog';
import { markConnectStepFinished } from './lib/connectStep';

const LAST_PROJECT_KEY = 'ceetrix_last_project';
const LAST_STORY_KEY = 'ceetrix_last_story';

export function getLastProject(): string | null {
  return localStorage.getItem(LAST_PROJECT_KEY);
}

export function setLastProject(projectSlug: string): void {
  localStorage.setItem(LAST_PROJECT_KEY, projectSlug);
}

export function getLastStory(): string | null {
  return localStorage.getItem(LAST_STORY_KEY);
}

export function setLastStory(storyId: string): void {
  localStorage.setItem(LAST_STORY_KEY, storyId);
}

function AppContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [currentTermsVersion, setCurrentTermsVersion] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [authSource, setAuthSource] = useState<'cli' | 'web' | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Track pageviews on route changes
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Skip auth check for test routes
  const isTestRoute = window.location.pathname.startsWith('/test');

  // Check authentication status on mount (skip for test routes)
  useEffect(() => {
    if (isTestRoute) {
      setIsAuthenticated(false); // Don't need auth for test routes
      return;
    }

    async function checkAuth() {
      try {
        const response = await fetch(`${config.apiUrl}/auth/me`, {
          credentials: 'include',
        });
        const data = await response.json() as {
          authenticated?: boolean;
          userId?: string;
          email?: string;
          termsAcceptedVersion?: string | null;
          currentTermsVersion?: string;
        };

        if (data.authenticated === false) {
          setIsAuthenticated(false);
          resetUser(); // Clear any stale identity
          return;
        }

        // User is authenticated
        setIsAuthenticated(true);

        // Check consent status (Story 463)
        if (data.currentTermsVersion) {
          setCurrentTermsVersion(data.currentTermsVersion);
          if (data.termsAcceptedVersion !== data.currentTermsVersion) {
            setNeedsConsent(true);
          }
        }

        // Identify user in PostHog
        if (data.userId) {
          identifyUser(data.userId, data.email);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
      }
    }

    checkAuth();
  }, [isTestRoute]);

  // Check for post-auth redirect
  useEffect(() => {
    const source = searchParams.get('source');
    const authStatus = searchParams.get('auth');

    if (authStatus === 'success') {
      setIsAuthenticated(true);

      // Only show modal for CLI auth, not web
      if (source === 'cli') {
        // Story 586: the person has just run the command the connect step asks for
        markConnectStepFinished();
        setAuthSource(source);
        setShowModal(true);
      }

      // Clear query params first
      setSearchParams({}, { replace: true });

      // For web sign-in (not CLI), redirect to last project if available
      if (source !== 'cli') {
        const lastProject = getLastProject();
        if (lastProject) {
          const lastStory = getLastStory();
          const params = new URLSearchParams();
          params.set('project', lastProject);
          if (lastStory) {
            params.set('story', lastStory);
          }
          navigate(`/workspace?${params.toString()}`, { replace: true });
          return;
        }
      }

      navigate('/dashboard', { replace: true });
    }
  }, [searchParams, setSearchParams, navigate]);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Loading state
  if (isAuthenticated === null) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-base)',
        }}
      >
        <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route
          path="/dashboard"
          element={isAuthenticated ? <Dashboard /> : <Navigate to="/signin" replace />}
        />
        <Route
          path="/workspace"
          element={isAuthenticated ? <Workspace /> : <Navigate to="/signin" replace />}
        />
        <Route
          path="/signups"
          element={isAuthenticated ? <SignupsAdmin /> : <Navigate to="/signin" replace />}
        />
        <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/signin"} replace />} />
        {/* Test routes - only available in development and staging */}
        {config.enableTestRoutes && (
          <>
            <Route path="/test/doc-editor" element={<DocEditorTest />} />
            <Route path="/test/story-browser" element={<StoryBrowserTest />} />
            <Route path="/test/story-detail" element={<StoryDetailTest />} />
            <Route path="/test/dashboard" element={<DashboardTest />} />
            <Route path="/test/generic-doc" element={<GenericDocTest />} />
            <Route path="/test/project-settings" element={<ProjectSettingsTest />} />
          </>
        )}
      </Routes>

      {showModal && authSource && (
        <PostAuthModal source={authSource} onClose={handleCloseModal} />
      )}

      {needsConsent && isAuthenticated && (
        <ConsentGate
          currentTermsVersion={currentTermsVersion}
          onAccepted={() => setNeedsConsent(false)}
        />
      )}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
